import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import { pick } from 'lodash';
import { useCallback } from 'react';
import * as yup from 'yup';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import { CURRENT_FOLDER_QK } from '@/modules/admin-dashboard/pages/Documents.tsx';
import {
  useCreateFolderMutation,
  useUpdateFolderMutation,
} from '@/modules/admin-dashboard/queries/documents-folders.ts';
import {
  BaseModalProps,
  Button,
  Color,
  Icon,
  ModalContentWrapper,
  SelectPaginateOption,
  TextInputField,
  mapColorToCssColor,
} from 'ui';
import { useParseNumericQueryParam } from 'ui/hooks';
import { FolderIcon } from 'ui/icons';
import { DocumentFolderTreeNode } from 'compliance-api';

const availableFolderColors = pick(mapColorToCssColor, ['yellow', 'blue', 'emerald', 'purple']);

const initialValues = {
  name: '',
  color: availableFolderColors.yellow,
  client_id: undefined as number | undefined,
  clientOption: undefined as SelectPaginateOption<number> | undefined,
};

const validationSchema = yup.object({
  name: yup.string().trim().min(3).max(100).required('Name is required'),
});

interface DocumentsFolderModalProps extends BaseModalProps {
  initialFolder?: DocumentFolderTreeNode;
  clientId?: number;
  hideClientSelector?: boolean;
}

const DocumentsFolderModal = ({
  initialFolder,
  open,
  onClose,
  clientId,
  hideClientSelector,
}: DocumentsFolderModalProps) => {
  const currentFolderId = useParseNumericQueryParam(CURRENT_FOLDER_QK);

  const { mutateAsync: createFolder, isLoading: isFolderCreation } = useCreateFolderMutation();

  const { mutateAsync: updateFolder, isLoading: isFolderUpdating } = useUpdateFolderMutation();

  const formik = useFormik({
    validationSchema,
    initialValues: initialFolder
      ? {
          ...pick(initialFolder, ['name', 'color']),
          client_id: clientId || initialFolder.client_id || undefined,
          clientOption:
            clientId && !initialFolder
              ? undefined
              : initialFolder.client
              ? {
                  value: initialFolder.client.id,
                  label: initialFolder.client.name,
                }
              : undefined,
        }
      : {
          ...initialValues,
          client_id: clientId || undefined,
        },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      if (initialFolder) {
        await updateFolder({
          folder_id: initialFolder.id,
          name: values.name,
          color: values.color,
          client_id: clientId || values.client_id,
        });
      } else {
        await createFolder({
          name: values.name,
          color: values.color,
          client_id: clientId || values.client_id,
          ...(currentFolderId ? { parent_folder_id: currentFolderId } : {}),
        });
      }

      onClose();
    },
  });

  const handleChangeColor = useCallback(
    (color: string) => {
      formik.setFieldValue('color', color);
    },
    [formik],
  );

  const handleSelectClient = useCallback(
    (newValue: SelectPaginateOption<number> | null) => {
      formik.setFieldValue('client_id', newValue?.value ?? null);
      formik.setFieldValue('clientOption', newValue);
    },
    [formik],
  );
  return (
    <ModalContentWrapper title="Create folder" open={open} onClose={onClose}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="space-y-5">
            <TextInputField name="name" label="Folder name" placeholder="Name..." />
            {!clientId && !hideClientSelector && (
              <ClientsSelectPaginated
                isMulti={false}
                value={formik.values.clientOption}
                onChange={handleSelectClient}
                isClearable={true}
              />
            )}
            <div className="flex gap-2">
              {(Object.entries(availableFolderColors) as [Color, string][]).map(
                ([name, color]: [Color, string]) => (
                  <div
                    key={name}
                    className={classNames(
                      'w-[42px] h-[42px] flex justify-center items-center rounded-full bg-[rgba(229,_229,_229,_0.5)] cursor-pointer',
                      { 'border border-black': color === formik.values.color },
                    )}
                    onClick={handleChangeColor.bind(null, color)}
                  >
                    <Icon Source={FolderIcon} rawColor={color} />
                  </div>
                ),
              )}
            </div>
            <Button
              type="submit"
              disabled={isFolderCreation || isFolderUpdating}
              loading={isFolderCreation || isFolderUpdating}
            >
              {initialFolder ? 'Save' : 'Add new folder'}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default DocumentsFolderModal;
